import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useActiveSection } from "../../hooks/useActiveSection";
import { goToSection } from "../../lib/sectionFlow";
import { DUR, EASE } from "../../lib/motion";
import "./Navbar.css";

type NavItem = { id: string; label: string; type: "section" | "route"; href?: string };

const navItems: NavItem[] = [
    { id: "home", label: "Home", type: "section" },
    { id: "about", label: "About", type: "section" },
    { id: "experience", label: "Experience", type: "section" },
    { id: "projects", label: "Projects", type: "section" },
    { id: "contact", label: "Contact", type: "section" },
    { id: "resume", label: "Resume", type: "route", href: "/resume" },
];

const sectionIds = navItems.filter((n) => n.type === "section").map((n) => n.id);

/**
 * The pill's contents: a normal label and an inverted-color twin stacked
 * beneath it, both clipped by the pill's own overflow:hidden. Hovering
 * (`.pill-fill` in Navbar.css) slides a fill up from the bottom edge and
 * carries both labels up with it in lockstep, so the visible one swaps from
 * the muted label to the inverted one exactly as the fill arrives — the
 * roll-up move React Bits' PillNav is known for.
 *
 * Rebuilt on CSS transitions rather than the upstream GSAP timeline: the
 * fill there is a precisely-radiused circle computed per pill on layout so
 * its flat edge lines up with the pill's own border-radius. That precision
 * buys a subtlety this rebuild trades for zero JS on hover and no second
 * animation engine — at this pill size, a rectangular wipe clipped by the
 * same border-radius reads the same to the eye.
 */
function PillContent({ label, active }: { label: string; active: boolean }) {
    return (
        <>
            {active && (
                <motion.span
                    layoutId="nav-pill"
                    className="nav-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
            )}
            <span className="pill-fill" aria-hidden="true" />
            <span className="pill-stack">
                <span className="nav-label">{label}</span>
                <span className="nav-label nav-label-hover" aria-hidden="true">
                    {label}
                </span>
            </span>
        </>
    );
}

export default function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const activeSection = useActiveSection(sectionIds, 80);
    const location = useLocation();

    /*
     * How far down the document the reader is, 0–1, driving the hairline
     * under the bar.
     *
     * Springed rather than bound raw: `scrollYProgress` updates in lockstep
     * with the scroll event, so a raw binding gives a line that is perfectly
     * accurate and completely lifeless — it just *is* wherever the page is.
     * A light spring lets it arrive a beat late, which is the only reason the
     * eye registers it as something moving rather than as a static gradient.
     */
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, {
        stiffness: 160,
        damping: 30,
        restDelta: 0.001,
    });

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            if (!goToSection(id)) el.scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.replaceState(null, "", `#${id}`);
        }
        setIsMobileMenuOpen(false);
    };

    const isActive = (item: NavItem) => {
        if (item.type === "route") {
            return location.pathname === item.href;
        }
        // A section item only reads as active on the landing route itself —
        // otherwise Home/About/Projects/Contact would all show active while
        // sitting on /resume, since useActiveSection has nothing on that page
        // to observe and never clears its last value.
        return location.pathname === "/" && activeSection === item.id;
    };

    return (
        <motion.nav
            className={`navbar ${isScrolled ? "scrolled" : ""}`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: DUR.base, ease: EASE }}
        >
            <div className="navbar-container">
                {location.pathname === "/" ? (
                    <button
                        type="button"
                        className="navbar-logo"
                        onClick={() => scrollTo("home")}
                        aria-label="Home"
                    >
                        <span className="logo-bracket">{"<"}</span>
                        <span className="logo-swap">
                            <span className="logo-text">QuocHuy</span>
                            <span className="logo-mark" aria-hidden="true">QH</span>
                        </span>
                        <span className="logo-bracket">{"/>"}</span>
                    </button>
                ) : (
                    <Link to="/" className="navbar-logo" aria-label="Home">
                        <span className="logo-bracket">{"<"}</span>
                        <span className="logo-swap">
                            <span className="logo-text">QuocHuy</span>
                            <span className="logo-mark" aria-hidden="true">QH</span>
                        </span>
                        <span className="logo-bracket">{"/>"}</span>
                    </Link>
                )}

                {/* Desktop Navigation */}
                <div className="navbar-links">
                    {navItems.map((item) => {
                        const active = isActive(item);

                        if (item.type === "route") {
                            return (
                                <Link
                                    key={item.id}
                                    to={item.href!}
                                    className={`navlink ${active ? "active" : ""}`}
                                >
                                    <PillContent label={item.label} active={active} />
                                </Link>
                            );
                        }

                        // On /resume there is nothing on-page to scroll to, so
                        // section items become links back to "/#id" instead of
                        // scroll handlers — following one first navigates home,
                        // and OnePage's own hash effect finishes the scroll.
                        if (location.pathname !== "/") {
                            return (
                                <Link
                                    key={item.id}
                                    to={`/#${item.id}`}
                                    className={`navlink ${active ? "active" : ""}`}
                                >
                                    <PillContent label={item.label} active={active} />
                                </Link>
                            );
                        }

                        return (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => scrollTo(item.id)}
                                className={`navlink ${active ? "active" : ""}`}
                            >
                                <PillContent label={item.label} active={active} />
                            </button>
                        );
                    })}
                </div>

                {/* Mobile Menu Button */}
                <button
                    className={`mobile-menu-btn ${isMobileMenuOpen ? "open" : ""}`}
                    onClick={() => setIsMobileMenuOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: DUR.quick, ease: EASE }}
                    >
                        {navItems.map((item) => {
                            const active = isActive(item);
                            const to =
                                item.type === "route"
                                    ? item.href!
                                    : location.pathname !== "/"
                                    ? `/#${item.id}`
                                    : null;

                            if (to) {
                                return (
                                    <Link
                                        key={item.id}
                                        to={to}
                                        className={`mobile-navlink ${active ? "active" : ""}`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                );
                            }
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => scrollTo(item.id)}
                                    className={`mobile-navlink ${active ? "active" : ""}`}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>

            {/*
                Reading progress. `transformOrigin: 0%` rather than a width
                animation on purpose — scaling a 1px bar is a composited
                transform the GPU handles for free, while animating width
                relayouts the bar on every single scroll frame.
            */}
            <motion.div
                className="navbar-progress"
                style={{ scaleX: progress }}
                aria-hidden="true"
            />
        </motion.nav>
    );
}
